import './style.scss';

import classNames from 'classnames';
import Error, { ERROR_CODES } from 'components/Error';
import Loading from 'components/Loading';
import usePreloadImage from 'helper/js/hooks/use-preload-image';
import { RefObject, useEffect, useRef, useState } from 'react';

type Props = {
  src: string;
  className?: string;
  alt?: string;
  draggable?: boolean;
  minimalError?: boolean;
  onLoad?: () => void;
  onError?: (src: string) => void;
  disableLazyLoading?: boolean;
  showLoading?: boolean;
  forceLazyLoading?: boolean;
  useSkeleton?: boolean;
  preloadImage?: boolean;
  preloadImageSrc?: string;
  fetchPriority?: 'high' | 'low' | 'auto';
};

const Img = ({
  className = 'img',
  src,
  alt = '',
  draggable = false,
  minimalError = false,
  onLoad = () => {},
  onError = () => {},
  disableLazyLoading = false,
  showLoading = true,
  forceLazyLoading = false,
  useSkeleton = false,
  preloadImage = false,
  preloadImageSrc,
  fetchPriority,
}: Props) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const image = usePreloadImage(
    preloadImage ? src : '',
    imgRef as RefObject<Element>,
    preloadImageSrc,
  );
  const currentSrc = preloadImage ? image : src;

  useEffect(() => {
    // Browser cache check: if already cached in memory, complete is true immediately
    // and onLoad will not fire. We must mark it loaded so the skeleton removes instantly.
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
      onLoad();
    } else {
      setIsLoaded(false);
    }
    setHasError(false);
  }, [currentSrc, onLoad, useSkeleton]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad();
  };

  const handleError = () => {
    if (!currentSrc) return; // Ignore empty string placeholders
    onError(currentSrc);
    setHasError(true);
  };

  if (hasError && currentSrc) {
    return <Error errorCode={ERROR_CODES.IMAGE} onlyImage={minimalError} />;
  }
  const shouldApplyLazyLoading =
    !preloadImage && !useSkeleton && (forceLazyLoading || !disableLazyLoading);

  if (useSkeleton) {
    return (
      <div
        className={classNames(className, 'img-container', {
          'img--skeleton': !isLoaded,
        })}>
        {currentSrc ? (
          <img
            ref={imgRef}
            className={classNames('img__content', {
              'img__content--loaded': isLoaded,
            })}
            src={currentSrc}
            onLoad={handleLoad}
            onError={handleError}
            alt={alt}
            draggable={draggable}
            decoding="async"
            style={{ opacity: isLoaded ? 1 : 0 }}
            {...(fetchPriority && { fetchpriority: fetchPriority })}
          />
        ) : null}
      </div>
    );
  }

  return (
    <>
      {!isLoaded && showLoading && currentSrc && <Loading />}
      {currentSrc ? (
        <img
          ref={imgRef}
          className={className}
          onError={handleError}
          onLoad={handleLoad}
          src={currentSrc}
          alt={alt}
          draggable={draggable}
          {...(fetchPriority && { fetchpriority: fetchPriority })}
          {...(shouldApplyLazyLoading && { loading: 'lazy' })}
        />
      ) : null}
    </>
  );
};

export default Img;
